// Server-authoritative player-resource read/write path (resources domain R1).
//
// Resources are PUBLIC (both players see each other's life/mana/thresholds), so
// the server projection never strips them. The actor mutates locally first
// (PlayerResources / recalculateSiteAffinities already wrote gameState.trackers
// + the HUD), THEN emits the command; the authoritative diff confirms it and
// propagates to the opponent. Replacing the legacy `tracker:set` P2P message.
//
// Naming bridge: the client tracker uses `wind`; the authoritative document
// uses `air`. Convert at this boundary, nowhere else.

import { SET_LIFE, SET_MANA, SET_THRESHOLDS } from '../../../utils/game/net/matchCore';

const THRESHOLD_KEYS = new Set(['earth', 'water', 'fire', 'wind']);

// client tracker {earth,water,fire,wind} → authoritative {air,earth,fire,water}
function thresholdsFromTracker(t = {}) {
  return { air: t.wind || 0, earth: t.earth || 0, fire: t.fire || 0, water: t.water || 0 };
}

// authoritative {air,earth,fire,water} → client tracker keys {earth,water,fire,wind}
function trackerFromThresholds(th = {}) {
  return { earth: th.earth || 0, water: th.water || 0, fire: th.fire || 0, wind: th.air || 0 };
}

// Build the authoritative command for a single tracker-key change, or null if
// the key isn't a synced resource. Life carries the seat's current Death's Door
// turn-key so immunity rides with the value.
export function resourceCommandForTracker(host, seat, key, value) {
  if (key === 'life') {
    const dd = host.playerResources?.serializeDeathsDoor?.() || {};
    return { type: SET_LIFE, payload: { seat, life: value, deathsDoorTurnKey: dd[seat] ?? null } };
  }
  if (key === 'mana') {
    return { type: SET_MANA, payload: { seat, mana: value } };
  }
  if (THRESHOLD_KEYS.has(key)) {
    const t = host.state.gameState?.trackers?.[seat] || {};
    return { type: SET_THRESHOLDS, payload: { seat, thresholds: thresholdsFromTracker(t) } };
  }
  return null;
}

// Fold the authoritative players sub-tree onto gameState.trackers + the DD
// stamps. Idempotent: only writes changed values, so the actor's own seat (set
// optimistically before the emit) is a no-op and the opponent's seat updates.
export function applyResourcesFromMirror(host, mirrorState) {
  const players = mirrorState?.players;
  const gs = host.state?.gameState;
  if (!players || !gs?.trackers) return;

  let changed = false;
  const trackers = { ...gs.trackers };
  const ddPatch = {};
  for (const seat of ['p1', 'p2']) {
    const p = players[seat];
    // Skip an unseeded seat: a seat's life is undefined until its SEED_RESOURCES
    // lands, and the rest of its fields default to 0 in a fresh MatchState.
    // Folding those defaults would clobber a live local mana/threshold value on
    // an unrelated diff (turn-pass). Once life is set the whole sub-tree is real.
    if (!p || p.life === undefined) continue;
    const cur = trackers[seat] || {};
    const next = { ...cur };
    if (p.life !== undefined && p.life !== cur.life) {
      // Damage feedback parity (ported from the legacy tracker:set relay.
      // this fold is now the ONLY life render path under auth): a life DROP
      // pops the floating number anchored on that player's avatar. Suppressed
      // during a fight. Combat life loss is staged on the combat overlay.
      const prev = Number(cur.life ?? 0);
      const dropped = Number(p.life) < prev;
      const inFight = !!(host.state.gameState?.pendingCombat || host.state.combatScene);
      if (dropped && !inFight && prev > 0) {
        const avatar = host._findAvatarForPlayer?.(seat === 'p2' ? 2 : 1);
        const avatarMesh = avatar && host.meshes?.get(avatar.id);
        if (avatarMesh) host._spawnFloatingDamage?.(avatarMesh, prev - Number(p.life), '#ef4444');
      }
      next.life = p.life;
      changed = true;
    }
    if (p.mana !== undefined && p.mana !== cur.mana) { next.mana = p.mana; changed = true; }
    if (p.thresholds) {
      const tt = trackerFromThresholds(p.thresholds);
      for (const k of ['earth', 'water', 'fire', 'wind']) {
        if (tt[k] !== cur[k]) { next[k] = tt[k]; changed = true; }
      }
    }
    trackers[seat] = next;
    if (p.deathsDoorTurnKey !== undefined) ddPatch[seat] = p.deathsDoorTurnKey;
  }

  if (Object.keys(ddPatch).length && host.playerResources?.restoreDeathsDoor) {
    host.playerResources.restoreDeathsDoor({ ...host.playerResources.serializeDeathsDoor?.(), ...ddPatch });
  }
  if (changed) {
    host.setState({ gameState: { ...gs, trackers } });
    host.updateTrackerTokenPositions?.();
  }
}
